import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Card, Collapse, Radio, Space, Switch, Tag, Tooltip, Typography, message } from 'antd';
import { EditOutlined, ReloadOutlined } from '@ant-design/icons';
import { ingestionStore } from '../store/IngestionStore';
import { ingestionApi } from '../ingestionApi';
import { catalogImageUrl } from '../../images';
import { fileNameOf, geometryGroupKey, sortedVariants, variationLabel } from '../ingestionHelpers';
import { VariantAssetViewer } from './VariantAssetViewer';
import { ModelEditor, latestOriginalGlb } from './ModelEditor';
import { MeshyGenConfig, PbrModeControl, PolycountInput, enablePbrOf, pbrModeOf } from './MeshyGenConfig';
import { MODEL_ENGINES, isBedType } from '../itemTypes';
import { BuilderRunPanel } from './BuilderRunPanel';

const { Text } = Typography;

const MODEL_TYPES = ['FloorItem', 'WallItem', 'Door', 'Crib', 'ToddlerBed', 'TwinBed', 'QueenBed', 'Bassinet'];
const engineOf = (item) => (item.modelEngine === 'opus' ? 'opus' : 'meshy');
const TEXTURE_TYPES = ['Texture', 'WallPrint', 'Pillow', 'Curtain'];

const assetSpec = (itemType, rugClass) => {
    // A rug's asset kind follows its class: complex → 3D model, flat (default) → texture.
    if (itemType === 'Rug' && rugClass === 'complex') {
        return { required: '3D model', accept: '.glb,.babylon', needed: true, model: true };
    }
    if (itemType === 'Rug') {
        return { required: 'texture image', accept: 'image/*', needed: true, texture: true };
    }
    if (MODEL_TYPES.includes(itemType)) {
        return { required: '3D model', accept: '.glb,.babylon', needed: true, model: true };
    }
    if (TEXTURE_TYPES.includes(itemType)) return { required: 'texture image', accept: 'image/*', needed: true, texture: true };
    return { required: 'none', accept: '', needed: false };
};

export const Step4Assets = observer(({ item }) => {
    const [byVariant, setByVariant] = useState({});
    const [loading, setLoading] = useState(false);
    const [modelEditorOpen, setModelEditorOpen] = useState(false);
    const spec = assetSpec(item.itemType, item.rugClass);
    const isBed = isBedType(item.itemType);
    const engine = engineOf(item);
    const opus = spec.model && engine === 'opus';

    const loadAssets = async () => {
        setLoading(true);
        try {
            const rows = await ingestionApi.getAssets(item.id);
            setByVariant(Object.fromEntries(rows.map((r) => [r.variantId, r])));
        } catch (e) {
            message.error(e.message || 'Failed to load assets');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadAssets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item.id]);

    // Meshy generation runs for minutes — poll while the step is running.
    useEffect(() => {
        if (item.status !== 'running') return undefined;
        const interval = setInterval(() => {
            ingestionStore.refreshCurrentItem(item.id);
            loadAssets();
        }, 4000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item.status, item.id]);

    const handleUpload = async (variantId, file) => {
        try {
            await ingestionApi.uploadAsset(item.id, variantId, file);
            message.success(`Uploaded ${file.name}`);
            await loadAssets();
        } catch (e) {
            message.error(e.message || 'Upload failed');
        }
        return false; // prevent antd's default upload
    };

    const handleUseAsAsset = async (variantId, source) => {
        try {
            const { applied } = await ingestionApi.useAsAsset(item.id, variantId, source);
            if (applied === 0) {
                message.info('This image is already the asset');
            } else {
                message.success(applied > 1 ? `Image set as the asset for ${applied} variants` : 'Image set as the asset');
            }
            const rows = await ingestionApi.getAssets(item.id);
            setByVariant(Object.fromEntries(rows.map((r) => [r.variantId, r])));
            // Every variant has its asset now — run the step so the item advances.
            if ((item.variants || []).every((v) => rows.find((r) => r.variantId === v.id)?.hasRequired)) {
                await ingestionStore.runStep(item.id, 4);
                await ingestionStore.refreshCurrentItem(item.id);
            }
        } catch (e) {
            message.error(e.message || 'Failed to set as asset');
        }
    };

    const handleDownload = async (asset, filename) => {
        try {
            const blob = await ingestionApi.getAssetBlob(item.id, asset.id);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            message.error(e.message || 'Download failed');
        }
    };

    const [regenerating, setRegenerating] = useState(null);
    const [retexturing, setRetexturing] = useState(null);
    const [configByVariant, setConfigByVariant] = useState({});

    // Model settings applied to ALL variants by the bulk "Generate model" action.
    // Seed the bulk controls from a variant's saved model config (defaults: PBR
    // auto, remove-lighting off, configured polycount) so they reflect the
    // last-applied settings instead of silently resetting them on the next generate.
    const savedModelCfg = (item.variants || []).map((v) => v.meshyConfig?.model).find(Boolean) || {};
    const [modelPbr, setModelPbr] = useState(pbrModeOf(savedModelCfg.enablePbr));
    const [modelRemoveLighting, setModelRemoveLighting] = useState(savedModelCfg.removeLighting === true);
    const [modelPolycount, setModelPolycount] = useState(savedModelCfg.targetPolycount ?? null);

    // A meshy job runs for minutes in the worker; keep the clicked variant's button
    // locked until the item leaves `running` (the poll above flips it back), not just
    // until the enqueue call returns. Clear the in-flight markers when it does. The
    // lock is per-variant (only the variant being processed), not the whole item.
    const itemRunning = item.status === 'running';
    useEffect(() => {
        if (!itemRunning) {
            setRegenerating(null);
            setRetexturing(null);
        }
    }, [itemRunning]);

    const handleRegenerate = async (variantId, feedback) => {
        setRegenerating(variantId);
        try {
            const body = opus ? (feedback ? { feedback } : undefined) : spec.model ? configByVariant[variantId] : undefined;
            await ingestionApi.regenerateVariantAsset(item.id, variantId, body);
            message.success(opus ? 'Building with Opus — this takes a few minutes' : 'Regenerating — this takes a few minutes');
            await ingestionStore.refreshCurrentItem(item.id);
            await loadAssets();
        } catch (e) {
            message.error(e.message || 'Failed to regenerate');
            setRegenerating(null);
        }
    };

    const handleRetexture = async (variantId, config) => {
        setRetexturing(variantId);
        try {
            await ingestionApi.retextureVariantAsset(item.id, variantId, config);
            message.success('Retexturing — this takes a few minutes');
            await ingestionStore.refreshCurrentItem(item.id);
            await loadAssets();
        } catch (e) {
            message.error(e.message || 'Failed to retexture');
            setRetexturing(null);
        }
    };

    const allReady = (item.variants || []).every((v) => !spec.needed || byVariant[v.id]?.hasRequired);

    const editableModelCount = spec.model
        ? (item.variants || []).filter((v) => latestOriginalGlb(byVariant[v.id])).length
        : 0;

    const hasModel = (v) => !!byVariant[v.id]?.hasRequired;
    // Variants differing only by color/texture share geometry — one model per
    // group is generated; the rest get a cheap retexture from it.
    const groups = {};
    (item.variants || []).forEach((v) => {
        const key = geometryGroupKey(item, v);
        (groups[key] = groups[key] || []).push(v);
    });
    const groupList = Object.values(groups);
    const repsReady = spec.model && groupList.length > 0 && groupList.every((g) => g.some(hasModel));
    // The Opus builder makes every variant from its own photos: no Meshy retexture of the rest.
    const textureRemainingState = spec.model && !opus && !allReady && repsReady;

    // Other variants whose model can serve as this variant's retexture geometry.
    const modelSourceOptionsOf = (variant) =>
        (item.variants || [])
            .filter((o) => o.id !== variant.id && hasModel(o))
            .map((o) => ({
                variantId: o.id,
                label: variationLabel(item, o),
                cutoutUrl: o.imageId ? catalogImageUrl(o.imageId, 512) : null,
            }));

    const handleRunStep = async () => {
        try {
            if (textureRemainingState) {
                const { queued } = await ingestionApi.textureRemaining(item.id);
                message.success(`Texturing ${queued} variant(s) — the item continues automatically when done`);
            } else {
                // Generating models (not yet all ready): apply the chosen texture
                // settings to every variant before the run picks them up.
                if (spec.model && !allReady) {
                    await ingestionApi.setModelConfig(item.id, {
                        enablePbr: enablePbrOf(modelPbr),
                        removeLighting: modelRemoveLighting,
                        targetPolycount: modelPolycount ?? undefined,
                    });
                }
                await ingestionStore.runStep(item.id, 4);
                message.success('Assets step queued');
            }
            await ingestionStore.refreshCurrentItem(item.id);
            await loadAssets();
        } catch (e) {
            message.error(e.message || 'Failed to run assets step');
        }
    };

    const [settingEngine, setSettingEngine] = useState(false);
    const handleEngine = async (modelEngine) => {
        setSettingEngine(true);
        try {
            await ingestionApi.setModelEngine(item.id, modelEngine);
            message.success(`Models by ${MODEL_ENGINES.find((e) => e.value === modelEngine)?.label || modelEngine} from the next generation`);
            await ingestionStore.refreshCurrentItem(item.id);
        } catch (e) {
            message.error(e.message || 'Failed to set the model engine');
        } finally {
            setSettingEngine(false);
        }
    };
    const [settingRugClass, setSettingRugClass] = useState(false);
    const handleRugClass = async (rugClass) => {
        setSettingRugClass(true);
        try {
            await ingestionApi.setRugClass(item.id, rugClass);
            message.success(`Rug set to ${rugClass === 'complex' ? 'complex 3D shape' : 'flat plane'}`);
            await ingestionStore.refreshCurrentItem(item.id);
            await loadAssets();
        } catch (e) {
            message.error(e.message || 'Failed to set rug class');
        } finally {
            setSettingRugClass(false);
        }
    };

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {item.itemType === 'Rug' && (
                <Card size="small">
                    <Space>
                        <Text strong>Rug class:</Text>
                        <Radio.Group
                            value={item.rugClass || 'flat'}
                            disabled={settingRugClass}
                            onChange={(e) => handleRugClass(e.target.value)}
                            options={[
                                { value: 'flat', label: 'Flat plane (texture)' },
                                { value: 'complex', label: 'Complex 3D shape (model)' },
                            ]}
                            optionType="button"
                        />
                        <Text type="secondary">
                            Flat rugs publish their top-down photo as a plane; complex shapes get a 3D model.
                        </Text>
                    </Space>
                </Card>
            )}
            {spec.model && (
                <Card size="small">
                    <Space wrap>
                        <Text strong>3D model by:</Text>
                        <Radio.Group
                            value={engine}
                            disabled={settingEngine || ingestionStore.runningStep}
                            onChange={(e) => handleEngine(e.target.value)}
                            options={MODEL_ENGINES.map((e) => ({ value: e.value, label: e.label }))}
                            optionType="button"
                        />
                    </Space>
                </Card>
            )}
            <Card>
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Text type="secondary">
                        {spec.model
                            ? textureRemainingState
                                ? 'Every geometry group has a model. "Texture remaining variants" retextures each remaining variant from its group\'s model (styled by its own cutout) and the item continues automatically when done.'
                                : opus
                                  ? null
                                  : isBed
                                  ? 'Beds are modeled as bare frames: the image model first removes the mattress and bedding from the photo, then meshy.ai builds the frame from that (one variant per geometry group; the rest are retextured from it). Add the mattress + blanket in the model editor, then run the step again to continue.'
                                  : 'A 3D model is auto-generated via meshy.ai for one variant per geometry group — variants differing only by color/texture are later retextured from it (takes a few minutes). Review it below — re-upload an edited .glb if needed — then run the step again to continue.'
                            : spec.texture
                              ? 'A diffuse texture is auto-generated per variant (a seamless tileable swatch for repeating textures; the source print for WallPrint) and the step advances automatically. Review below and upload your own image to override.'
                              : 'This item type needs no asset files. Run the step to advance.'}
                    </Text>
                    <Space>
                        {spec.model && (
                            <Tooltip title={editableModelCount === 0 ? 'No variant has a .glb model yet' : undefined}>
                                <Button
                                    icon={<EditOutlined />}
                                    disabled={editableModelCount === 0}
                                    onClick={() => setModelEditorOpen(true)}
                                >
                                    Model editor
                                </Button>
                            </Tooltip>
                        )}
                        <Button icon={<ReloadOutlined />} onClick={loadAssets} loading={loading}>
                            Refresh
                        </Button>
                        <Button
                            type="primary"
                            loading={ingestionStore.runningStep}
                            onClick={handleRunStep}
                        >
                            {spec.model
                                ? allReady
                                    ? 'Approve & continue'
                                    : textureRemainingState
                                      ? 'Texture remaining variants'
                                      : 'Generate model'
                                : spec.texture
                                  ? allReady ? 'Run assets step' : 'Generate texture'
                                  : 'Run assets step'}
                        </Button>
                    </Space>
                </Space>
                {spec.model && !opus && (
                    <Collapse
                        size="small"
                        style={{ marginTop: 12 }}
                        items={[
                            {
                                key: 'texture-settings',
                                label: 'Model settings (applied to all variants on “Generate model”)',
                                children: (
                                    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
                                        <PbrModeControl value={modelPbr} onChange={setModelPbr} />
                                        <span>
                                            <Switch
                                                checked={modelRemoveLighting}
                                                onChange={setModelRemoveLighting}
                                            />{' '}
                                            <Text>Remove baked lighting</Text>
                                        </span>
                                        <PolycountInput value={modelPolycount} onChange={setModelPolycount} />
                                    </div>
                                ),
                            },
                        ]}
                    />
                )}
                {spec.needed && !allReady && (
                    <Alert
                        style={{ marginTop: 12 }}
                        type="warning"
                        showIcon
                        message="Some variants are missing their asset"
                        description={
                            spec.texture
                                ? 'Run the step to auto-generate a texture for each variant, or upload your own below.'
                                : textureRemainingState
                                  ? 'Click "Texture remaining variants" to retexture them from their group’s model, or upload a model manually below.'
                                  : `Upload a ${spec.required} for each variant below, then run the step.`
                        }
                    />
                )}
            </Card>

            {spec.needed &&
                sortedVariants(item).map((variant) => {
                    const row = byVariant[variant.id];
                    const assets = row?.assets || [];
                    return (
                        <Card
                            key={variant.id}
                            size="small"
                            title={variationLabel(item, variant)}
                            extra={
                                row?.hasRequired ? (
                                    <Tag color="success">asset ready</Tag>
                                ) : (
                                    <Tag color="warning">no asset</Tag>
                                )
                            }
                        >
                            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                {variant.meshyConfig?.lastError && (
                                    <Alert
                                        type="error"
                                        showIcon
                                        message="Asset generation failed"
                                        description={variant.meshyConfig.lastError}
                                    />
                                )}
                                {opus && (
                                    <BuilderRunPanel
                                        itemId={item.id}
                                        variantId={variant.id}
                                        run={variant.builderRun}
                                        runs={variant.builderRuns}
                                        hasModel={!!row?.hasRequired}
                                        onRebuild={(feedback) => handleRegenerate(variant.id, feedback)}
                                        busy={regenerating === variant.id || !!ingestionStore.runningStep}
                                    />
                                )}
                                <VariantAssetViewer
                                    itemId={item.id}
                                    variantId={variant.id}
                                    assets={assets}
                                    cutoutImageId={variant.imageId}
                                    modelImageUrls={spec.model ? variant.meshyConfig?.model?.imageUrls : undefined}
                                    bedFrameImageIds={
                                        isBed ? Object.values(variant.meshyConfig?.model?.bedFrameImages || {}) : undefined
                                    }
                                    uploadAccept={spec.accept}
                                    uploadLabel={`Upload ${spec.required}`}
                                    onUpload={(file) => handleUpload(variant.id, file)}
                                    onDownload={(a) => handleDownload(a, fileNameOf(a, variant.id))}
                                    onUseAsAsset={spec.texture ? (source) => handleUseAsAsset(variant.id, source) : undefined}
                                />

                                {spec.texture && (
                                    <Tooltip title="Drops this variant's texture and generates it again from its current image">
                                        <Button
                                            size="small"
                                            icon={<ReloadOutlined />}
                                            loading={regenerating === variant.id}
                                            disabled={!variant.imageId || regenerating === variant.id}
                                            onClick={() => handleRegenerate(variant.id)}
                                        >
                                            Regenerate texture
                                        </Button>
                                    </Tooltip>
                                )}

                                {spec.model && !opus && (
                                    <MeshyGenConfig
                                        itemId={item.id}
                                        variant={variant}
                                        isBed={isBed}
                                        onModelChange={(cfg) =>
                                            setConfigByVariant((prev) => ({ ...prev, [variant.id]: cfg }))
                                        }
                                        onRegenerate={() => handleRegenerate(variant.id)}
                                        regenerating={regenerating === variant.id}
                                        onRetexture={(cfg) => handleRetexture(variant.id, cfg)}
                                        hasOwnModel={!!row?.hasRequired}
                                        modelSourceOptions={modelSourceOptionsOf(variant)}
                                        retexturing={retexturing === variant.id}
                                        busy={regenerating === variant.id || retexturing === variant.id}
                                    />
                                )}
                            </Space>
                        </Card>
                    );
                })}

            {/* Mounted (closed) as soon as the step renders: the Babylon engine boots
                and every variant's latest original .glb preloads in the background,
                so the editor opens instantly. */}
            {spec.model && (
                <ModelEditor
                    open={modelEditorOpen}
                    item={item}
                    assetsByVariant={byVariant}
                    onClose={() => setModelEditorOpen(false)}
                    onSaved={loadAssets}
                />
            )}
        </Space>
    );
});
